<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { getRedactedClientPhoneNumber, sendPasscodeApi } from '@Api/otp.api';
import { VerificationCodeProviderType } from '@Enums/verificationCodeProviderType.enum';
import useFlowNavigation from '@Use/useFlowNavigation';
import { ShowcaseLayout, ActionBarLayout } from '@Shared';
import useCompanyImageBaseUrl from '@Use/useCompanyImageBaseUrl';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import { useAuthSessionStore } from '@Stores/authSession.store';
import type { ValidationRules } from '@Shared';
import { useFormValidation, useFieldStatus} from '@Shared';
import ShowcaseImage from '@Components/ShowcaseImage.vue';

// Stores / base data
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const authSessionStore = useAuthSessionStore();
const { advanceFlow } = useFlowNavigation();
const companyImageBaseUrl = useCompanyImageBaseUrl();
const phoneLoading = ref(true);
const preFillPhoneNumber = ref(false);
const redactedPhoneNumber = ref('');

// Form state
const formData = ref({ phoneNumber: '' });
const isWorking = ref(false);
// Form validation
const validationRules = ref<ValidationRules>({
    'phoneNumber': [
    { required: true, message: 'Phone number is required' },
    { validatePhone: true, message: 'Please enter a valid U.S. phone number' }
    ],
});
const formValidation = useFormValidation(formData, validationRules);
const { turnOnValidation, hasError: hasFormError } = formValidation;
const {getFieldStatus, getFieldMessage } = useFieldStatus(formValidation);

// Actions
const sendPasscode = async () => {
    isWorking.value = true;
    try {
        await turnOnValidation(false);
        if (preFillPhoneNumber.value || !hasFormError.value) {
            await sendPasscodeApi(VerificationCodeProviderType.Sms, formData.value.phoneNumber);
            authSessionStore.setPhoneNumber(formData.value.phoneNumber);
            advanceFlow();
        }
    } finally {
        isWorking.value = false;
    }
}
// Lifecycle hooks
onMounted(async () => {
    try {
        // If the 4006 full-login handoff (or send-code fallback) seeded a phone
        // number, pre-fill the form and leave the user in the editable form
        // presentation rather than the masked last-four presentation.
        if (authSessionStore.phoneNumber) {
            formData.value.phoneNumber = authSessionStore.phoneNumber;
            preFillPhoneNumber.value = false;
            return;
        }

        redactedPhoneNumber.value = await getRedactedClientPhoneNumber();
        preFillPhoneNumber.value = true;
    } finally {
        phoneLoading.value = false;
    }
});
</script>
<template>
    <showcase-layout v-if="!phoneLoading"
        suppress-back-to-dashboard
        title="Verify your phone number"
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl">
        <div v-if="preFillPhoneNumber">
            <div class="client-description">
                To do this, we’ll send you a security code.
            </div>
            <br></br>
            <div class="device-validate-phone">
                <div class="device-validate-phone__number">
                    {{ redactedPhoneNumber }}
                </div>
                <br></br>
                <div class="device-validate-phone__link">
                    <app-link link-url="https://certifidhelpcenter.zendesk.com/hc/en-us/articles/18427088389915-What-if-my-phone-number-is-wrong-">Is this the wrong number?</app-link>
                </div>
            </div>
        </div>
        <app-form-content-layout  v-else>
            <p>
                Enter your phone number to receive a one-time code.
            </p>
            <app-masked-input
                v-model="formData.phoneNumber"
                auto-focus
                is-required
                label="Phone number"
                mask="(###) ###-####"
                input-mode="numeric"
                placeholder="Enter phone number"
                :field-status="getFieldStatus('phoneNumber')"
                :message="getFieldMessage('phoneNumber')"
                data-validation-field="phoneNumber"
                bottomOffset="2.6rem"
                data-id="phoneNumber">
                <template #focus-message>
                    U.S. numbers only
                </template>
            </app-masked-input>
        </app-form-content-layout>

        <action-bar-layout>
            <app-button
                expanded
                size="large"
                :is-loading="isWorking"
                @onClick="sendPasscode">
                    Send Code
            </app-button>
        </action-bar-layout>

        <template #branded>
            <showcase-image />
        </template>

    </showcase-layout>
    <app-page-loader v-if="phoneLoading" />
    
</template>
<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

::v-deep(.focus-message-container) {
    position: absolute;
}
.device-validate-phone {
    text-align: center;

    &__number {
        color: $theme-primary;
        font-size: 1.25rem;
        font-weight: 700;
    }

    &__link {
        font-size: $caption-text;
    }
}
</style>